import { useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import PageHeader from "@/components/PageHeader";
import CtaBand from "@/components/CtaBand";
import { FadeUp, Overline, MaskedLines } from "@/components/Reveal";

const FAQS = [
  {
    q: "What does bookkeeping cost?",
    a: "It depends on the size and complexity of your business. A business with one bank account and relatively few monthly transactions may only need basic reconciliation and financial reporting, while a growing company with multiple accounts, payroll, sales tax, or job costing will naturally require more involvement. Pricing is based on what your business actually needs — not a predetermined package filled with services you may never use.",
    testid: "faq-item-cost",
  },
  {
    q: "Do I need to be good with numbers to work with you?",
    a: "No. Many business owners are excellent at what they do but would rather not spend their evenings reconciling accounts, sorting through QuickBooks, or trying to make sense of financial reports. Our role is to make that side of the business easier to manage.",
    testid: "faq-item-numbers",
  },
  {
    q: "Can you help if my books are behind?",
    a: "Yes. Catch-up and cleanup bookkeeping is one of our core services — we bring past transactions up to date, reconcile accounts, and get your records organized so you can move forward with accurate numbers.",
    testid: "faq-item-catchup",
  },
  {
    q: "Do you prepare taxes for individuals as well as businesses?",
    a: "Yes. Arnold Bookkeeping prepares federal, Pennsylvania, and local income tax returns for individuals, as well as small-business returns for sole proprietors, single-member LLCs, partnerships, and S corporations.",
    testid: "faq-item-taxes",
  },
  {
    q: "Can you help with Pennsylvania sales tax?",
    a: "Yes. We prepare and file Pennsylvania sales tax returns, review the sales tax collected and amounts due, and provide use tax reporting support.",
    testid: "faq-item-sales-tax",
  },
  {
    q: "What accounting software do you support?",
    a: "We work with QuickBooks Online — including setup, cleanup, and ongoing support — and we can help you understand what your reports are actually telling you about your business.",
    testid: "faq-item-software",
  },
  {
    q: "Where do you work with clients?",
    a: "Yes — Arnold Bookkeeping serves small businesses near Wellsville, Pennsylvania, the surrounding areas, and across Central Pennsylvania. When you work with Arnold Bookkeeping, you work directly with founder Aaron Arnold.",
    testid: "faq-item-area",
  },
  {
    q: "How do I get started?",
    a: "Complete the short Get Started form or call 717.554.1224. Arnold Bookkeeping can review your current setup, discuss what you actually need, and provide pricing based on the scope of the work.",
    testid: "faq-item-started",
  },
];

export default function Faq() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById("faq-jsonld")?.remove();
    };
  }, []);

  return (
    <div>
      <PageHeader
        testid="faq-header"
        overline="FAQ"
        lines={["Questions, answered", "plainly."]}
        intro="Straight answers about working with Arnold Bookkeeping — pricing, tax preparation, QuickBooks, and who we serve."
      />

      <section className="pb-24" data-testid="faq-section">
        <div className="mx-auto max-w-4xl px-6">
          <FadeUp>
            <Overline>Common questions</Overline>
          </FadeUp>
          <Accordion type="single" collapsible className="mt-8" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <FadeUp key={f.q} delay={i * 0.04}>
                <AccordionItem value={`faq-${i}`} data-testid={f.testid} className="border-hairline">
                  <AccordionTrigger className="py-6 text-left font-serif text-xl font-semibold text-ink hover:text-pine hover:no-underline sm:text-2xl">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base leading-relaxed text-stone2">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              </FadeUp>
            ))}
          </Accordion>

          <FadeUp delay={0.2}>
            <div className="mt-16 rounded-2xl bg-parchment p-10 text-center" data-testid="faq-still-questions">
              <MaskedLines
                as="h2"
                inView
                lines={["Still have questions?"]}
                className="font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl"
              />
              <p className="mx-auto mt-4 max-w-md text-stone2">
                Reach out directly — you'll hear back from Aaron Arnold, not a call center.
              </p>
              <p className="mt-4 font-mono text-xs uppercase tracking-[0.25em] text-pine/70">
                Wellsville &amp; Central Pennsylvania
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
